import { isRecord, jsonObject } from "./json-body";
import type { CacheWrite } from "./usage";

const FIVE_MINUTES = 300;
const THIRTY_MINUTES = 1800;
const ONE_HOUR = 3600;
const TWELVE_HOURS = 43_200;

const openaiLifetimes: Record<string, number> = { "30m": THIRTY_MINUTES, "12h": TWELVE_HOURS };

/**
 * The cache lifetimes a request asked for. An Anthropic `cache_control` breakpoint lasts five
 * minutes unless its `ttl` says `1h`. An OpenAI `prompt_cache_breakpoint` lasts its own `ttl`, else
 * the request's `prompt_cache_options.ttl`, else thirty minutes; in implicit mode, OpenAI's default,
 * the prompt's end is a breakpoint too.
 */
function requestedLifetimes(requestBody: string): Set<number> {
  const body = jsonObject(requestBody);
  const options = isRecord(body.prompt_cache_options) ? body.prompt_cache_options : undefined;
  const openaiDefault =
    (typeof options?.ttl === "string" && openaiLifetimes[options.ttl]) || THIRTY_MINUTES;
  const lifetimes = new Set<number>();
  if (options && options.mode !== "explicit") lifetimes.add(openaiDefault);
  const visit = (value: unknown) => {
    if (Array.isArray(value)) return value.forEach(visit);
    if (!isRecord(value)) return;
    if (isRecord(value.cache_control))
      lifetimes.add(value.cache_control.ttl === "1h" ? ONE_HOUR : FIVE_MINUTES);
    if (isRecord(value.prompt_cache_breakpoint)) {
      const ttl = value.prompt_cache_breakpoint.ttl;
      lifetimes.add((typeof ttl === "string" && openaiLifetimes[ttl]) || openaiDefault);
    }
    Object.values(value).forEach(visit);
  };
  visit(body);
  return lifetimes;
}

/**
 * Cache writes a response reported as one total, with the lifetime the request asked for. A request
 * that asked for several lifetimes leaves the mix open; one that asked for none leaves it to the
 * SKU, whose model may keep writes for only one.
 */
export function requestedCacheWrites(tokens: number, requestBody: string): CacheWrite[] {
  if (tokens <= 0) return [];
  const lifetimes = [...requestedLifetimes(requestBody)].sort((left, right) => left - right);
  if (lifetimes.length === 1) return [{ tokens, ttlSeconds: lifetimes[0] }];
  if (lifetimes.length > 1) return [{ tokens, possibleTtlSeconds: lifetimes }];
  return [{ tokens }];
}

export { FIVE_MINUTES, ONE_HOUR, THIRTY_MINUTES, TWELVE_HOURS };
